import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { useRegisterCadastrosPageActions } from "../layout";
import { STATUS_COLORS, STATUS_LABELS, formatDateTime } from "../shared/status";

const findOption = (options, codigo) =>
  options.find((opt) => opt.codigo === codigo) || null;

const withOrphanOption = (options, codigo) => {
  if (!codigo || findOption(options, codigo)) return options;
  return [
    { codigo, descricao: codigo, label: `${codigo} (não listado)`, invalido: true },
    ...options,
  ];
};

const DetalheSolicitacao = () => {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sol, setSol] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [perms, setPerms] = useState({ solicitar: false, estoque: false });

  const [um, setUm] = useState("");
  const [conta, setConta] = useState("");
  const [ncm, setNcm] = useState("");
  const [b1Cod, setB1Cod] = useState("");
  const [comentario, setComentario] = useState("");
  const [sugerindoCodigo, setSugerindoCodigo] = useState(false);
  const [sugestaoInfo, setSugestaoInfo] = useState(null);
  const [contas, setContas] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);
  const [ncms, setNcms] = useState([]);
  const [loadingNcm, setLoadingNcm] = useState(false);
  const [ncmInput, setNcmInput] = useState("");

  const [dialog, setDialog] = useState(null);

  const contasOptions = useMemo(() => withOrphanOption(contas, conta), [contas, conta]);
  const contaSelecionada = useMemo(
    () => findOption(contasOptions, conta),
    [contasOptions, conta]
  );
  const contaValida = useMemo(
    () => Boolean(conta && findOption(contas, conta)),
    [contas, conta]
  );

  const ncmsOptions = useMemo(() => withOrphanOption(ncms, ncm), [ncms, ncm]);
  const ncmSelecionado = useMemo(() => findOption(ncmsOptions, ncm), [ncmsOptions, ncm]);
  const ncmValido = useMemo(() => Boolean(ncm && findOption(ncms, ncm)), [ncms, ncm]);
  const podeFinalizarMaster = contaValida && ncmValido;

  const sugerirProximoCodigo = async (solicitacao, { forcar = false } = {}) => {
    if (!solicitacao?.grupo) {
      setSugestaoInfo({
        ok: false,
        message: "Preencha o grupo na solicitação para sugerir o próximo código.",
      });
      return;
    }
    setSugerindoCodigo(true);
    try {
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/ProximoCodigo/index.php?id=${solicitacao.id}&grupo=${encodeURIComponent(solicitacao.grupo)}`
      );
      const proximo = data.data?.proximo_codigo || "";
      setSugestaoInfo({
        ok: Boolean(data.success && proximo),
        message: data.message,
        proximo,
        ultimos: data.data?.ultimos_codigos || [],
        quantidade: data.data?.quantidade_ocupados || 0,
      });
      if (data.success && proximo && (forcar || !solicitacao.b1_cod)) {
        setB1Cod(proximo);
      }
    } catch (e) {
      setSugestaoInfo({
        ok: false,
        message: e?.response?.data?.message || "Erro ao sugerir próximo código.",
      });
    } finally {
      setSugerindoCodigo(false);
    }
  };

  const carregarContas = async () => {
    setLoadingContas(true);
    try {
      const { data } = await api.get("Painel/API/Cadastros/Produtos/ListarOpcoes/index.php");
      setContas(data.data?.contas || []);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar contas contábeis (CT1).");
    } finally {
      setLoadingContas(false);
    }
  };

  const buscarNcm = async (termo) => {
    const q = String(termo || "").trim();
    if (q.length < 2) {
      setNcms((prev) => {
        const atual = ncm ? findOption(prev, ncm) : null;
        return atual && !atual.invalido ? [atual] : [];
      });
      return;
    }
    setLoadingNcm(true);
    try {
      const params = new URLSearchParams({ q, limit: "40" });
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/ListarNcm/index.php?${params.toString()}`
      );
      const lista = data.data || [];
      setNcms((prev) => {
        const selecionado = ncm ? findOption(prev, ncm) : null;
        if (selecionado && !selecionado.invalido && !findOption(lista, selecionado.codigo)) {
          return [selecionado, ...lista];
        }
        return lista;
      });
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao buscar NCM (SYD).");
    } finally {
      setLoadingNcm(false);
    }
  };

  const carregarNcmSelecionado = async (codigo) => {
    const cod = String(codigo || "").trim();
    if (!cod) return;
    try {
      const params = new URLSearchParams({ codigo: cod, limit: "1" });
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/ListarNcm/index.php?${params.toString()}`
      );
      const item = (data.data || [])[0];
      if (item) {
        setNcms((prev) => (findOption(prev, item.codigo) ? prev : [item, ...prev]));
      }
    } catch {
      // Mantém o código salvo mesmo se a busca falhar; validação ocorre no save.
    }
  };

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/Solicitacao/index.php?id=${id}`
      );
      setSol(data.data);
      setHistorico(data.historico || []);
      setPerms(data.permissoes || { solicitar: false, estoque: false });
      setUm(data.data.unidade_medida || "");
      setConta(data.data.conta_contabil || "");
      setNcm(data.data.ncm || "");
      setNcmInput(data.data.ncm || "");
      setB1Cod(data.data.b1_cod || "");

      if (data.data.status === "AGUARDANDO_MASTER") {
        await carregarContas();
        if (data.data.ncm) {
          await carregarNcmSelecionado(data.data.ncm);
        }
      }

      if (data.data.status === "EM_CADASTRO" && data.data.grupo) {
        await sugerirProximoCodigo(data.data, { forcar: !data.data.b1_cod });
      } else {
        setSugestaoInfo(null);
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar solicitação.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [id]);

  useEffect(() => {
    if (sol?.status !== "AGUARDANDO_MASTER") return undefined;
    const handle = setTimeout(() => {
      buscarNcm(ncmInput);
    }, 350);
    return () => clearTimeout(handle);
  }, [ncmInput, sol?.status]);

  useRegisterCadastrosPageActions({
    onRefresh: carregar,
    refreshing: loading,
    pageTitle: sol ? `Solicitação #${sol.id}` : `Solicitação #${id}`,
    pageSubtitle: sol
      ? STATUS_LABELS[sol.status] || sol.status
      : "Carregando detalhes…",
  });

  const postAction = async (url, body, successMsg) => {
    setSaving(true);
    try {
      await api.post(url, body);
      notifySuccess(successMsg);
      setDialog(null);
      setComentario("");
      await carregar();
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao executar ação.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !sol) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const podeEditar =
    perms.solicitar && ["RASCUNHO", "DEVOLVIDO_ESTOQUE"].includes(sol.status);
  const podeEstoque = perms.estoque && sol.status === "AGUARDANDO_ESTOQUE";
  const podeMaster = perms.solicitar && sol.status === "AGUARDANDO_MASTER";
  const podeCadastrar = perms.solicitar && sol.status === "EM_CADASTRO";
  const podeCancelar =
    perms.solicitar &&
    !["CADASTRADO", "JA_EXISTENTE", "CANCELADO"].includes(sol.status);
  const podeExcluir = perms.solicitar;
  const temWorkflow = podeEstoque || podeMaster || podeCadastrar;

  const excluirSolicitacao = async () => {
    setSaving(true);
    try {
      await api.post("Painel/API/Cadastros/Produtos/Excluir/index.php", {
        id: sol.id,
        comentario: comentario.trim() || "Solicitação excluída por estar incorreta.",
      });
      notifySuccess("Solicitação excluída.");
      setDialog(null);
      setComentario("");
      navigate("/modulo/cadastros/produtos", { replace: true });
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao excluir solicitação.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, height: "100%", overflow: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={1.5}
        mb={2}
      >
        <Stack spacing={0.75}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
            Solicitação #{sol.id}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={STATUS_LABELS[sol.status] || sol.status}
              color={STATUS_COLORS[sol.status] || "default"}
            />
            <Typography variant="caption" color="text.secondary">
              Criada em {formatDateTime(sol.created_at)}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {podeEditar && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/modulo/cadastros/produtos/${sol.id}/editar`)}
              fullWidth={isMobile}
              sx={{ minHeight: 44 }}
            >
              Editar
            </Button>
          )}
          {podeCancelar && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => setDialog("cancelar")}
              fullWidth={isMobile}
              sx={{ minHeight: 44 }}
            >
              Cancelar
            </Button>
          )}
          {podeExcluir && (
            <Button
              color="error"
              variant="contained"
              onClick={() => setDialog("excluir")}
              fullWidth={isMobile}
              sx={{ minHeight: 44 }}
            >
              Excluir
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <Stack spacing={2} sx={{ flex: 1.2, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Dados da solicitação
            </Typography>
            <Stack spacing={1.25}>
              <Field
                label="Solicitante"
                value={`${sol.solicitante_nome || "—"} (${sol.solicitante_email || "—"})`}
              />
              <Field label="Descrição" value={sol.descricao} />
              <Field label="Descrição reduzida" value={sol.descricao_reduzida} />
              <Field label="Tipo / Grupo" value={`${sol.tipo || "—"} / ${sol.grupo || "—"}`} />
              <Field label="Justificativa" value={sol.justificativa} />
              <Field label="Origem (e-mail)" value={sol.origem_email} />
              <Divider />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ "& > *": { flex: 1 } }}
              >
                <Field label="Unidade de medida" value={sol.unidade_medida} />
                <Field label="Conta contábil" value={sol.conta_contabil} />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ "& > *": { flex: 1 } }}
              >
                <Field label="NCM" value={sol.ncm} />
                <Field label="Código Protheus" value={sol.b1_cod || sol.b1_cod_similar} />
              </Stack>
              <Field label="Justificativa não similar" value={sol.justificativa_nao_similar} />
              <Field label="Consulta SB1 em" value={formatDateTime(sol.consulta_sb1_em)} />
            </Stack>
          </Paper>

          {temWorkflow && (
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Ações do fluxo
              </Typography>
              <Stack spacing={1.5}>
                {podeEstoque && (
                  <Stack spacing={1}>
                    <TextField
                      label="Unidade de medida *"
                      value={um}
                      onChange={(e) => setUm(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="contained"
                        color="secondary"
                        disabled={saving}
                        fullWidth={isMobile}
                        sx={{ minHeight: 44 }}
                        onClick={() =>
                          postAction(
                            "Painel/API/Cadastros/Produtos/DefinirUM/index.php",
                            { id: sol.id, acao: "APROVAR", unidade_medida: um },
                            "UM formalizada."
                          )
                        }
                      >
                        Confirmar UM
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setDialog("devolver")}
                        fullWidth={isMobile}
                        sx={{ minHeight: 44 }}
                      >
                        Devolver
                      </Button>
                    </Stack>
                  </Stack>
                )}

                {podeMaster && (
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Autocomplete
                        options={contasOptions}
                        value={contaSelecionada}
                        loading={loadingContas}
                        onChange={(_, option) => {
                          if (option?.invalido) {
                            setConta("");
                            notifyError(
                              "Selecione uma conta contábil válida da lista oficial de produtos."
                            );
                            return;
                          }
                          setConta(option?.codigo || "");
                        }}
                        getOptionLabel={(option) => option?.label || option?.codigo || ""}
                        isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                        getOptionDisabled={(option) => Boolean(option?.invalido)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Conta contábil *"
                            placeholder="Buscar na CT1..."
                            size="small"
                            required
                            error={Boolean(conta) && !contaValida}
                            helperText={
                              contaValida
                                ? `Código: ${conta}`
                                : conta
                                  ? "Conta inválida ou fora da lista permitida. Selecione uma conta válida."
                                  : "Selecione uma conta da lista oficial de produtos"
                            }
                          />
                        )}
                        fullWidth
                      />
                      <Autocomplete
                        options={ncmsOptions}
                        value={ncmSelecionado}
                        inputValue={ncmInput}
                        loading={loadingNcm}
                        onChange={(_, option) => {
                          if (option?.invalido) {
                            setNcm("");
                            setNcmInput("");
                            notifyError(
                              "Selecione um NCM válido cadastrado na tabela SYD do Protheus."
                            );
                            return;
                          }
                          setNcm(option?.codigo || "");
                          setNcmInput(option?.codigo || "");
                        }}
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            setNcmInput(value);
                            if (ncm && value !== ncm) setNcm("");
                          }
                          if (reason === "clear") {
                            setNcmInput("");
                            setNcm("");
                          }
                        }}
                        filterOptions={(options) => options}
                        getOptionLabel={(option) => option?.label || option?.codigo || ""}
                        isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                        getOptionDisabled={(option) => Boolean(option?.invalido)}
                        noOptionsText={
                          ncmInput.trim().length < 2
                            ? "Digite ao menos 2 caracteres"
                            : loadingNcm
                              ? "Buscando…"
                              : "NCM não cadastrado na tabela SYD. Verifique se o código está correto ou se a tabela precisa ser atualizada no Protheus."
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="NCM *"
                            placeholder="Buscar na SYD (código ou descrição)"
                            size="small"
                            required
                            error={
                              Boolean(ncm && !ncmValido) ||
                              (!loadingNcm &&
                                !ncm &&
                                ncmInput.trim().length >= 2 &&
                                ncms.length === 0)
                            }
                            helperText={
                              ncmValido
                                ? `Código: ${ncm}`
                                : ncm && !ncmValido
                                  ? "NCM inválido ou não cadastrado na SYD. Selecione um NCM válido."
                                  : !loadingNcm &&
                                      ncmInput.trim().length >= 2 &&
                                      ncms.length === 0
                                    ? "NCM não cadastrado na SYD. Confira o código ou solicite a atualização da tabela de NCM no Protheus."
                                    : "Digite ao menos 2 caracteres e selecione um NCM da tabela SYD"
                            }
                          />
                        )}
                        fullWidth
                      />
                    </Stack>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={saving || !podeFinalizarMaster}
                      fullWidth={isMobile}
                      sx={{ minHeight: 44 }}
                      onClick={() => {
                        if (!contaValida) {
                          notifyError(
                            "Informe uma conta contábil válida da lista oficial antes de finalizar."
                          );
                          return;
                        }
                        if (!ncmValido) {
                          notifyError(
                            "Informe um NCM válido cadastrado na SYD antes de finalizar."
                          );
                          return;
                        }
                        postAction(
                          "Painel/API/Cadastros/Produtos/FinalizarMaster/index.php",
                          { id: sol.id, conta_contabil: conta, ncm },
                          "Liberado para cadastro no Protheus."
                        );
                      }}
                    >
                      Finalizar e liberar cadastro
                    </Button>
                  </Stack>
                )}

                {podeCadastrar && (
                  <Stack spacing={1.25}>
                    <Stack
                      spacing={1}
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "flex-start" }}
                    >
                      <TextField
                        label="Código B1 sugerido *"
                        value={b1Cod}
                        onChange={(e) => setB1Cod(e.target.value.toUpperCase())}
                        size="small"
                        fullWidth
                        helperText={
                          sugestaoInfo?.ok
                            ? `Grupo ${sol.grupo}: próximo livre na SB1 (Protheus), com base em ${sugestaoInfo.quantidade} código(s) já cadastrados.`
                            : sugestaoInfo?.message ||
                              (sol.grupo
                                ? `Grupo: ${sol.grupo}`
                                : "Informe o grupo na solicitação para numeração automática.")
                        }
                      />
                      <Button
                        variant="outlined"
                        disabled={saving || sugerindoCodigo || !sol.grupo}
                        onClick={() => sugerirProximoCodigo(sol, { forcar: true })}
                        fullWidth={isMobile}
                        sx={{ minHeight: 44, flexShrink: 0 }}
                      >
                        {sugerindoCodigo ? "Gerando..." : "Gerar próximo"}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={saving || !b1Cod.trim()}
                        fullWidth={isMobile}
                        sx={{ minHeight: 44, flexShrink: 0 }}
                        onClick={() =>
                          postAction(
                            "Painel/API/Cadastros/Produtos/Cadastrar/index.php",
                            { id: sol.id, b1_cod: b1Cod },
                            "Produto cadastrado."
                          )
                        }
                      >
                        Confirmar cadastro
                      </Button>
                    </Stack>
                    {sugestaoInfo?.ultimos?.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Últimos na SB1: {sugestaoInfo.ultimos.join(", ")}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}

          {!temWorkflow && !podeEditar && (
            <Typography variant="body2" color="text.secondary">
              Nenhuma ação de fluxo disponível para o seu perfil neste status.
            </Typography>
          )}
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, flex: 1, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Histórico
          </Typography>
          <Stack spacing={1.75}>
            {historico.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sem eventos.
              </Typography>
            ) : (
              historico.map((h) => (
                <Box
                  key={h.id}
                  sx={{
                    borderLeft: "3px solid",
                    borderColor: "secondary.main",
                    pl: 1.75,
                    py: 0.25,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {(h.de_status && `${STATUS_LABELS[h.de_status] || h.de_status} → `) || ""}
                    {STATUS_LABELS[h.para_status] || h.para_status}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {h.user_nome || `Usuário #${h.user_id}`} · {formatDateTime(h.created_at)}
                  </Typography>
                  {h.comentario && (
                    <Typography variant="body2" sx={{ mt: 0.75 }} color="text.secondary">
                      {h.comentario}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={dialog === "devolver"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Devolver para Master Data</DialogTitle>
        <DialogContent>
          <TextField
            label="Motivo *"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Fechar</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={() =>
              postAction(
                "Painel/API/Cadastros/Produtos/DefinirUM/index.php",
                { id: sol.id, acao: "DEVOLVER", comentario },
                "Solicitação devolvida."
              )
            }
          >
            Devolver
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "cancelar"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cancelar solicitação</DialogTitle>
        <DialogContent>
          <TextField
            label="Motivo *"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Fechar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={() =>
              postAction(
                "Painel/API/Cadastros/Produtos/Cancelar/index.php",
                { id: sol.id, comentario },
                "Solicitação cancelada."
              )
            }
          >
            Cancelar solicitação
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "excluir"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Excluir solicitação</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
            A solicitação será ocultada (soft delete) e não aparecerá mais nas listagens. Diferente
            de cancelar, ela some da tela do usuário.
          </Typography>
          <TextField
            label="Motivo (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Fechar</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={excluirSolicitacao}>
            Confirmar exclusão
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.15 }}>
      {value || "—"}
    </Typography>
  </Box>
);

export default DetalheSolicitacao;
